import type { Card } from "@/context/AppContext";
import type { ProductOpportunity } from "@/types/productOpportunity";

export interface ValidationResult {
  errors: string[];
  warnings: string[];
}

/**
 * Validate a ProductOpportunity before it's saved as a Product. Returns
 * separate `errors` (must be fixed) and `warnings` (acknowledge before saving).
 *
 * Rules per spec:
 *   - selected cards must be Unlisted with quantity > 0
 *   - cards already packaged in another product cannot be re-used
 *   - damaged cards require a warning unless title says "damaged"
 *   - mixed-game decks require a warning
 *   - decks claiming complete must have 60 cards
 *   - decks claiming Standard/Expanded legality require legalityVerified
 *   - non-basic-energy copies cannot exceed 4 in complete decks
 *   - target sale price must be > 0
 *   - negative margin warns user before saving
 */
export function validateProductLot(
  opportunity: ProductOpportunity,
  inventoryById: Map<string, Card>,
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // ── Card-level checks ──
  for (const cardId of opportunity.cardIds) {
    const card = inventoryById.get(cardId);
    if (!card) {
      errors.push(`Card ${cardId} no longer exists in inventory.`);
      continue;
    }
    if (card.listingStatus === "Listed" || card.listingStatus === "Sold") {
      errors.push(
        `${card.name} is currently ${card.listingStatus} and cannot be packaged.`,
      );
    }
    if (
      card.listingStatus === "In Package" &&
      card.packagedProductId &&
      card.packagedProductId !== opportunity.id
    ) {
      errors.push(
        `${card.name} is already in another product (${card.packagedProductId}).`,
      );
    }
    if ((card.quantity ?? 0) <= 0) {
      errors.push(`${card.name} has zero quantity.`);
    }
  }

  // ── Damaged & mixed-game warnings ──
  const titleLower = opportunity.title.toLowerCase();
  const hasDamaged = opportunity.cards.some(
    (c) => c.condition === "Damaged" || c.condition === "HP",
  );
  if (hasDamaged && !titleLower.includes("damaged")) {
    warnings.push(
      "Lot includes damaged or HP cards — add 'damaged' to the title or remove them.",
    );
  }
  const games = new Set(opportunity.cards.map((c) => c.game));
  if (games.size > 1 && !titleLower.includes("mixed")) {
    warnings.push(
      "Lot includes cards from multiple games — clearly label this in the title.",
    );
  }

  // ── Pricing checks ──
  if (opportunity.expectedSalePrice <= 0) {
    errors.push("Target sale price must be greater than zero.");
  }
  if (opportunity.expectedProfit < 0) {
    warnings.push(
      `Expected profit is negative ($${opportunity.expectedProfit.toFixed(2)}). Consider raising the price or removing low-margin cards.`,
    );
  }

  // ── Deck-specific checks ──
  const deckBuild = opportunity.deckBuild;
  if (deckBuild) {
    if (deckBuild.isCompleteDeck) {
      const total = deckBuild.deckList.reduce((s, l) => s + l.includedQty, 0);
      if (total !== 60) {
        errors.push(
          `Pre-built deck claims to be complete but has ${total} cards (expected 60).`,
        );
      }
      // Non-basic-energy copy limit.
      for (const line of deckBuild.deckList) {
        if (looksLikeBasicEnergy(line.name)) continue;
        if (line.includedQty > 4) {
          errors.push(
            `${line.name} has ${line.includedQty} copies in a complete deck — non-basic-energy max is 4.`,
          );
        }
      }
    }
    const claimsLegality =
      deckBuild.format === "Standard" ||
      deckBuild.format === "Expanded" ||
      deckBuild.format === "GLC" ||
      deckBuild.format === "Pocket";
    if (claimsLegality && !deckBuild.legalityVerified) {
      // Legality is unverified in MVP — warn so the seller doesn't accidentally
      // claim Standard legal in marketing copy.
      warnings.push(
        `Format is set to ${deckBuild.format} but legality is not verified. Description will not claim tournament legality.`,
      );
    }
  }

  return { errors, warnings };
}

function looksLikeBasicEnergy(name: string): boolean {
  const lower = name.toLowerCase();
  if (!lower.includes("energy")) return false;
  return /(fire|water|grass|lightning|psychic|fighting|darkness|metal|fairy|dragon|colorless)\s+energy/.test(
    lower,
  );
}
